import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TelegramIcon from "@mui/icons-material/Telegram";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useI18n, type Lang } from "../lib/i18n";
import { Button, Modal } from "./ui";

type Provider = "telegram" | "lark";

interface GStep {
  text: string;
  code?: string;
  link?: { label: string; url: string };
}
interface GProvider {
  intro: string;
  steps: GStep[];
}
interface GDict {
  title: string;
  subtitle: string;
  stepWord: string;
  note: string;
  tabs: Record<Provider, string>;
  providers: Record<Provider, GProvider>;
}

const GUIDE: Record<Lang, GDict> = {
  vi: {
    title: "Hướng dẫn thiết lập kênh thông báo",
    subtitle:
      "Làm theo các bước dưới đây để lấy thông tin cấu hình, sau đó Thêm kênh và bấm Gửi thử.",
    stepWord: "Bước",
    note: "Mẹo: có thể bật nhiều kênh cùng lúc — tất cả đều nhận cảnh báo. Bật “Gửi cả trạng thái nghi bị chặn” để nhận cả link unknown; đặt “Chống spam” vài phút để tránh nhận trùng khi một link lỗi liên tục.",
    tabs: {
      telegram: "Telegram",
      lark: "Lark · Nhóm (App API)",
    },
    providers: {
      telegram: {
        intro: "Gửi cảnh báo vào một group hoặc tới một người dùng Telegram.",
        steps: [
          {
            text: "Mở Telegram, chat với @BotFather, gửi /newbot rồi đặt tên + username cho bot. BotFather trả về Bot Token.",
            link: { label: "Mở @BotFather", url: "https://t.me/BotFather" },
          },
          {
            text: "Thêm bot vừa tạo vào group cần nhận cảnh báo (hoặc bấm Start nếu muốn gửi riêng cho bạn).",
          },
          {
            text: "Lấy Chat ID: thêm @userinfobot vào group, nó sẽ hiện Chat ID (group thường là số âm).",
            code: "Cách khác: gửi 1 tin trong group rồi mở\nhttps://api.telegram.org/bot<TOKEN>/getUpdates\nvà tìm \"chat\":{\"id\": -100...}",
            link: { label: "Mở @userinfobot", url: "https://t.me/userinfobot" },
          },
          {
            text: "(Tuỳ chọn) Nếu group bật Topics, mở topic muốn gửi và lấy Thread ID.",
          },
          {
            text: "Dùng chung 1 Bot cho nhiều nước: Tạo 1 Bot Telegram, sau đó trên CMS tạo 2 kênh (1 kênh chọn quốc gia Việt Nam 🇻🇳, 1 kênh chọn Brazil 🇧🇷), dùng chung Bot Token nhưng điền Chat ID hoặc Topic/Thread ID riêng.",
          },
          {
            text: "Tại CMS: Thêm kênh → chọn Telegram → chọn Quốc gia, dán Bot Token và Chat ID → Lưu → Gửi thử (hoặc chọn “Gửi thử log lỗi thiết bị” trong menu thao tác).",
          },
        ],
      },
      lark: {
        intro:
          "Dùng Lark App API (giống Telegram): App ID + App Secret. Chọn nhóm nhận theo 1 trong 2 cách — mọi nhóm bot đang ở (khuyến nghị) hoặc 1 nhóm cố định.",
        steps: [
          {
            text: "Vào Lark/Feishu Developer Console → Create custom app. Lấy App ID (cli_…) và App Secret ở mục Credentials.",
            link: { label: "Mở Developer Console", url: "https://open.larksuite.com/app" },
          },
          {
            text: "Vào Permissions & Scopes, thêm quyền im:message (gửi tin) + im:chat:readonly (để liệt kê nhóm — bắt buộc cho chế độ “mọi nhóm”) rồi Release/publish app.",
          },
          {
            text: "Thêm bot (app) vào từng nhóm cần nhận cảnh báo: mở nhóm → Settings → Bots → Add.",
          },
          {
            text: "Tại CMS: Thêm kênh → chọn Lark (nhóm) → chọn máy chủ, dán App ID / App Secret.",
          },
          {
            text: "Chọn “Nhóm nhận cảnh báo”: Mọi nhóm bot đang ở (khuyến nghị) — không cần chat_id, server tự liệt kê nhóm mỗi lần gửi, thêm/bớt bot khỏi nhóm là danh sách tự đổi theo. Bấm “Tải danh sách nhóm” để xem trước.",
          },
          {
            text: "Hoặc chọn “Một nhóm cố định” nếu chỉ muốn gửi đúng 1 nhóm — mở ô Chat ID nhóm và chọn theo tên. Cuối cùng Lưu → Gửi thử.",
          },
        ],
      },
    },
  },
  en: {
    title: "Notification channel setup guide",
    subtitle:
      "Follow the steps below to obtain the config values, then Add channel and hit Send test.",
    stepWord: "Step",
    note: "Tip: you can enable several channels at once — all of them receive alerts. Turn on “Also send suspected-blocked state” to include unknown links; set an anti-spam cooldown to avoid duplicates when a link keeps failing.",
    tabs: {
      telegram: "Telegram",
      lark: "Lark · Group (App API)",
    },
    providers: {
      telegram: {
        intro: "Send alerts into a group or to an individual Telegram user.",
        steps: [
          {
            text: "Open Telegram, chat with @BotFather, send /newbot then set a name + username. BotFather returns a Bot Token.",
            link: { label: "Open @BotFather", url: "https://t.me/BotFather" },
          },
          {
            text: "Add the new bot to the group that should receive alerts (or press Start to DM yourself).",
          },
          {
            text: "Get the Chat ID: add @userinfobot to the group, it shows the Chat ID (groups are usually negative).",
            code: "Alternative: post a message in the group, then open\nhttps://api.telegram.org/bot<TOKEN>/getUpdates\nand find \"chat\":{\"id\": -100...}",
            link: { label: "Open @userinfobot", url: "https://t.me/userinfobot" },
          },
          { text: "(Optional) If the group has Topics enabled, open the target topic to get its Thread ID." },
          {
            text: "Sharing 1 Bot across countries: You can create a single Telegram bot, then add 2 Channels in the CMS (one for Vietnam 🇻🇳, one for Brazil 🇧🇷) sharing the same Bot Token, but configured with separate Chat IDs or Topic/Thread IDs.",
          },
          {
            text: "In the CMS: Add channel → pick Telegram → choose Country, paste Bot Token and Chat ID → Save → Send test (or click “Send test device error alert” in the row actions menu).",
          },
        ],
      },
      lark: {
        intro:
          "Use the Lark App API (like Telegram): App ID + App Secret. Pick recipients one of two ways — every group the bot is in (recommended) or one fixed group.",
        steps: [
          {
            text: "Go to the Lark/Feishu Developer Console → Create custom app. Copy App ID (cli_…) and App Secret from Credentials.",
            link: { label: "Open Developer Console", url: "https://open.larksuite.com/app" },
          },
          {
            text: "Under Permissions & Scopes, add im:message (send messages) + im:chat:readonly (to list groups — required for all-groups mode), then Release/publish the app.",
          },
          {
            text: "Add the bot (app) to every group that should receive alerts: open the group → Settings → Bots → Add.",
          },
          {
            text: "In the CMS: Add channel → pick Lark (group) → choose server, paste App ID / App Secret.",
          },
          {
            text: "Set “Groups to alert” to Every group the bot is in (recommended) — no chat_id needed, the server lists the groups on every send, so adding or removing the bot updates the list automatically. Hit “Load groups” to preview.",
          },
          {
            text: "Or pick “One fixed group” to target a single group — the group Chat ID selector appears, choose it by name. Then Save → Send test.",
          },
        ],
      },
    },
  },
  zh: {
    title: "通知渠道设置指南",
    subtitle: "按以下步骤获取配置信息，然后点击“添加渠道”并“发送测试”。",
    stepWord: "步骤",
    note: "提示：可同时启用多个渠道 — 全部都会收到告警。开启“同时发送疑似被拦截状态”可包含 unknown 链接；设置防刷冷却时间可避免链接持续失败时重复收到。",
    tabs: {
      telegram: "Telegram",
      lark: "Lark · 群（App API）",
    },
    providers: {
      telegram: {
        intro: "向一个群组或某个 Telegram 用户发送告警。",
        steps: [
          {
            text: "打开 Telegram，与 @BotFather 对话，发送 /newbot 并设置名称和用户名。BotFather 会返回 Bot Token。",
            link: { label: "打开 @BotFather", url: "https://t.me/BotFather" },
          },
          { text: "把新建的机器人加入需要接收告警的群（或点击 Start 私聊给自己）。" },
          {
            text: "获取 Chat ID：把 @userinfobot 加入群，它会显示 Chat ID（群通常是负数）。",
            code: "另一种方法：在群里发一条消息，然后打开\nhttps://api.telegram.org/bot<TOKEN>/getUpdates\n查找 \"chat\":{\"id\": -100...}",
            link: { label: "打开 @userinfobot", url: "https://t.me/userinfobot" },
          },
          { text: "（可选）若群开启了话题（Topics），打开目标话题获取 Thread ID。" },
          {
            text: "多地区共用 1 个机器人：只需创建 1 个 Telegram Bot，然后在 CMS 创建 2 个渠道（分别选择越南 🇻🇳 与巴西 🇧🇷），共用同一个 Bot Token，但填写各自的 Chat ID 或 Topic/Thread ID。",
          },
          { text: "在 CMS：添加渠道 → 选择 Telegram → 选择国家，粘贴 Bot Token 和 Chat ID → 保存 → 发送测试（也可在行操作菜单中点击“测试设备报错”）。" },
        ],
      },
      lark: {
        intro:
          "使用 Lark App API（与 Telegram 类似）：App ID + App Secret。接收群有两种方式 — 机器人所在的全部群（推荐）或固定一个群。",
        steps: [
          {
            text: "进入 Lark/Feishu 开发者后台 → 创建自定义应用。在“凭证”中复制 App ID（cli_…）和 App Secret。",
            link: { label: "打开开发者后台", url: "https://open.larksuite.com/app" },
          },
          {
            text: "在权限管理中添加 im:message（发送消息）+ im:chat:readonly（用于获取群列表，全部群模式必需），然后发布应用。",
          },
          {
            text: "把机器人（应用）加入每一个需要接收告警的群：打开群 → 设置 → 机器人 → 添加。",
          },
          {
            text: "在 CMS：添加渠道 → 选择 Lark（群）→ 选择服务器，粘贴 App ID / App Secret。",
          },
          {
            text: "把“接收告警的群”设为机器人所在的全部群（推荐）— 无需 chat_id，服务器每次发送时自动列出群，机器人加入/退出群，列表随之变化。点击“加载群列表”可预览。",
          },
          {
            text: "若只想发给一个群，选择“固定一个群”，然后在群 Chat ID 中按名称选择。最后保存 → 发送测试。",
          },
        ],
      },
    },
  },
};

const TAB_ICON: Record<Provider, typeof TelegramIcon> = {
  telegram: TelegramIcon,
  lark: ForumOutlinedIcon,
};

/** Một bước hướng dẫn: số thứ tự + nội dung + (tuỳ chọn) đoạn mã / liên kết. */
function StepRow({ n, step }: { n: number; step: GStep }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!step.code) return;
    try {
      await navigator.clipboard.writeText(step.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard bị chặn — bỏ qua */
    }
  }

  return (
    <Stack direction="row" sx={{ gap: 1.5, alignItems: "flex-start" }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          flex: "none",
          mt: "1px",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          fontSize: 13,
          fontWeight: 700,
          color: "primary.main",
          bgcolor: theme =>
            theme.palette.mode === "dark" ? "rgba(59,130,246,0.16)" : "rgba(37,99,235,0.1)",
        }}
      >
        {n}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
          {step.text}
        </Typography>

        {step.code && (
          <Box
            sx={{
              position: "relative",
              mt: 1,
              p: 1.25,
              pr: 4.5,
              borderRadius: 1.5,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                color: "text.secondary",
              }}
            >
              {step.code}
            </Box>
            <Tooltip title={copied ? "✓" : "Copy"}>
              <IconButton
                size="small"
                onClick={copy}
                sx={{ position: "absolute", top: 4, right: 4 }}
              >
                {copied ? (
                  <CheckIcon fontSize="inherit" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="inherit" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {step.link && (
          <Link
            href={step.link.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {step.link.label}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
      </Box>
    </Stack>
  );
}

/** Modal hướng dẫn thiết lập kênh Telegram / Lark — nội dung theo ngôn ngữ đang chọn. */
export function ChannelSetupGuide({ onClose }: { onClose: () => void }) {
  const { lang, t } = useI18n();
  const g = GUIDE[lang] ?? GUIDE.vi;
  const [tab, setTab] = useState<Provider>("telegram");
  const p = g.providers[tab];
  const providers: Provider[] = ["telegram", "lark"];

  return (
    <Modal
      title={g.title}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("common.close")}
        </Button>
      }
    >
      <Typography variant="body2" color="text.secondary">
        {g.subtitle}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as Provider)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider", mt: -0.5 }}
      >
        {providers.map(pv => {
          const Icon = TAB_ICON[pv];
          return (
            <Tab
              key={pv}
              value={pv}
              icon={<Icon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={g.tabs[pv]}
              sx={{ minHeight: 44 }}
            />
          );
        })}
      </Tabs>

      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {p.intro}
      </Typography>

      <Stack sx={{ gap: 2 }}>
        {p.steps.map((s, i) => (
          <StepRow key={i} n={i + 1} step={s} />
        ))}
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mt: 0.5 }}>
        {g.note}
      </Alert>
    </Modal>
  );
}
